import { cloneDeep, isEqual, property } from "lodash";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { stringSortAscComparator } from "../../utils/arrayUtils";
import MdaeButton from "../common_widgets/button";
import MdaePopup, {
  MdaePopupContent,
  MdaePopupFooter,
  MdaePopupFooterEndButtons,
  MdaePopupFooterStartButtons,
  MdaePopupTitle,
} from "../common_widgets/popup";
import MdaeSelectBox from "../common_widgets/select-box/component";
import { BULK_UPDATE_CONFIGURATIONS, BULK_UPDATE_CONFIG_NAMES, externalAuditCaseFieldNames } from "./bulk-update-configuration";
import { BulkUpdateValueField } from "./bulk-update-value-field";

import "./index.scss";
import {
  ADD_BULK_UPDATE_ROW,
  EXECUTE_BULK_UPDATE,
  REMOVE_BULK_UPDATE_ROW,
  RESET_BULK_UPDATE_ROWS,
  TOGGLE_BULK_UPDATE_POPUP,
  UPDATE_BULK_UPDATE_ROW,
} from "./reducers/bulk-update-popup-reducer";
interface IBulkUpdatePopup {
  saveCallbackHandler: any;
  selectedEntitiesCountLabel?: string;
}
interface IBulkUpdateRowItem {
  currentFieldName?: string;
  fieldName: string;
  fieldValue: any;
  sequenceNumber: number;
  savePropertyName: string;
  selectedFieldNames: Array<string>;
}
export const hideBulkUpdatePopup = (dispatch) => {
  dispatch({
    type: TOGGLE_BULK_UPDATE_POPUP,
    data: {
      isBulkUpdatePopupVisible: false,
    },
  });
};
export const createFinalSaveObject = (currentBulkUpdateRows) => {
  const finalSaveObject: any = {};
  if (currentBulkUpdateRows) {
    for (let i = 0; i < currentBulkUpdateRows.length; i++) {
      finalSaveObject[currentBulkUpdateRows[i].savePropertyName] =
        currentBulkUpdateRows[i].fieldValue;
    }
  }

  return finalSaveObject;
};
const getCurrentBulkUpdateFieldNames = (
  currentBulkUpdateConfigurationName,
  currentBulkUpdateConfiguration,
  selectedFieldNames,
  currentFieldName
) => {
  let propertyList = [];
  if(currentBulkUpdateConfiguration?.length > 0){
    currentBulkUpdateConfiguration.forEach(currentBulkUpdateConfigurationItem => {
      if (
        !selectedFieldNames.includes(
          currentBulkUpdateConfigurationItem.displayName
        ) ||
        currentFieldName === currentBulkUpdateConfigurationItem.displayName
      )
        propertyList.push({
          fieldName: currentBulkUpdateConfigurationItem.displayName,
          savePropertyName:
            currentBulkUpdateConfigurationItem.saveValuePropertyName,
        });
    });

    //if case final outcome is selected then remove Advance to next round and vice versa
    if(currentBulkUpdateConfigurationName === BULK_UPDATE_CONFIG_NAMES.EXTERNAL_AUDIT_CASE){
      if(selectedFieldNames.includes(externalAuditCaseFieldNames.CASE_FINAL_OUTCOME))
        propertyList = propertyList.filter(x => x.fieldName !== externalAuditCaseFieldNames.ADVANCE_TO_NEXT_ROUND);
      else if(selectedFieldNames.includes(externalAuditCaseFieldNames.ADVANCE_TO_NEXT_ROUND))
        propertyList = propertyList.filter(x => x.fieldName !== externalAuditCaseFieldNames.CASE_FINAL_OUTCOME);
    }
  }
  return propertyList?.sort(stringSortAscComparator('fieldName'));
};
const defaultBulkUpdateRowData = {
  fieldName: null,
  fieldValue: null,
  sequenceNumber: 0,
  savePropertyName: null,
};
const addNewBulkUpdateRow = (dispatch) => {
  dispatch({
    type: ADD_BULK_UPDATE_ROW,
    data: cloneDeep(defaultBulkUpdateRowData),
  });
};

const BulkUpdateRowItem: React.FC<IBulkUpdateRowItem> = (props) => {
  const currentBulkUpdateConfigurationName = useSelector(
    (state) =>
      state["bulkUpdatePopupReducer"].currentBulkUpdateConfigurationName,
    isEqual
  ); 
  const dispatch = useDispatch();

  const currentBulkUpdateConfiguration =
    BULK_UPDATE_CONFIGURATIONS[currentBulkUpdateConfigurationName];
  const currentPropertyFieldList = getCurrentBulkUpdateFieldNames(
    currentBulkUpdateConfigurationName,
    currentBulkUpdateConfiguration,
    props.selectedFieldNames,
    props.fieldName
  );

  return (
    <>
      <div className={"bulk-update-row-field-name-container "}>
        <MdaeSelectBox
          id={""}
          width={"100%"}
          mdaeDropDownBoxTitle={""}
          value={props.fieldName}
          displayExpr={"fieldName"}
          valueExpr={"fieldName"}
          dataSource={currentPropertyFieldList}
          onValueChanged={(e) => {
            const selectedItem = e.component.option("selectedItem");
            dispatch({
              type: UPDATE_BULK_UPDATE_ROW,
              data: {
                sequenceNumber: props.sequenceNumber,
                fieldName: e.value,
                fieldValue: null,
                savePropertyName: selectedItem?.savePropertyName,
              },
            });
          }}
        ></MdaeSelectBox>
      </div>
      <div className={"bulk-update-row-field-value-container"}>
        <BulkUpdateValueField
          currentSequenceNumber={props.sequenceNumber}
          currentBulkUpdateFieldName={props.fieldName}
          currentBulkUpdateConfigurationName={
            currentBulkUpdateConfigurationName
          }
          currentBulkUpdateFieldValue={props.fieldValue}
        ></BulkUpdateValueField>
      </div>
      <div className={"bulk-updae-row-action-button-container"}>
        <div className={"remove-field-button"}>
          <MdaeButton
            id={""}
            role={"dxButton"}
            stylingMode={"text"}
            onClick={() => {
              dispatch({
                type: REMOVE_BULK_UPDATE_ROW,
                data: {
                  sequenceNumber: props.sequenceNumber,
                },
              });
            }}
          >
            <i
              className={
                "delete-action-icon bulk-update-row-action-remove-icon"
              }
            ></i>
          </MdaeButton>
        </div>
      </div>
    </>
  );
};
const BulkUpdatePopup: React.FC<IBulkUpdatePopup> = (props) => {
  const dispatch = useDispatch();
  const currentBulkUpdateRows = useSelector(
    (state) => state["bulkUpdatePopupReducer"].currentBulkUpdateRows,
    isEqual
  );
  const currentBulkUpdateConfigurationName = useSelector(
    (state) =>
      state["bulkUpdatePopupReducer"].currentBulkUpdateConfigurationName,
    isEqual
  );
  const executeBulkUpdate = useSelector(
    (state) =>
      state["bulkUpdatePopupReducer"].executeBulkUpdate,
    isEqual
  );
  const ignoreNoAdditionalRoundWarning = useSelector(
    (state) =>
      state["bulkUpdatePopupReducer"].ignoreNoAdditionalRoundWarning,
    isEqual
  ); 
  useEffect(() =>{
    if(executeBulkUpdate){
      dispatch({type: EXECUTE_BULK_UPDATE, data: {executeBulkUpdate: false} });
      props
        .saveCallbackHandler(createFinalSaveObject(currentBulkUpdateRows), false, ignoreNoAdditionalRoundWarning)
        .then(function(config) {
          if (config) {
            if (config.isClosePopup) hideBulkUpdatePopup(dispatch);
            else if (config.isResetPopup)
              dispatch({ type: RESET_BULK_UPDATE_ROWS });
          }
        });
    }
  }, [executeBulkUpdate])
  const currentBulkUpdateConfiguration =
    BULK_UPDATE_CONFIGURATIONS[currentBulkUpdateConfigurationName];
  const isAddNewRowVisible = () => {
    if (!currentBulkUpdateConfiguration) return true;
    const propertyList = [];
    for (let i = 0; i < currentBulkUpdateConfiguration.length; i++) {
      const fieldNameItemList = currentBulkUpdateRows?.filter(
        (x) => x.fieldName === currentBulkUpdateConfiguration[i].displayName
      );
      if (fieldNameItemList.length <= 0)
        propertyList.push(currentBulkUpdateConfiguration[i].displayName);
    }
    const lastBulkUpdateRow =
      currentBulkUpdateRows[currentBulkUpdateRows.length - 1];

    return (
      propertyList.length > 0 &&
      ((lastBulkUpdateRow?.fieldName &&
        lastBulkUpdateRow?.fieldValue != null) ||
        !currentBulkUpdateRows?.length)
    );
  };
 
  return (
    <div>
      <MdaePopup
        deferRendering={false}
        height={610}
        width={600}
        onShowing={() => {
          dispatch({ type: RESET_BULK_UPDATE_ROWS });
          addNewBulkUpdateRow(dispatch);
        }}
        id={"pnlBulkUpdatePopup"}
        mdaeTitleRender={() => {
          return (
            <MdaePopupTitle
              closePopupHandler={() => {
                hideBulkUpdatePopup(dispatch);
              }}
            >
              Bulk Update
            </MdaePopupTitle>
          );
        }}
        mdaeDataUpdaterConfig={[
          {
            actionType: TOGGLE_BULK_UPDATE_POPUP,
            reducerName: "bulkUpdatePopupReducer",
            widgetOptionBindings: {
              visible: "isBulkUpdatePopupVisible",
            },
          },
        ]}
        mdaeContentRender={() => {
          return (
            <div>
              <MdaePopupContent>
                {props.selectedEntitiesCountLabel && (
                  <div style={{ fontWeight: 600 }}>
                    {props.selectedEntitiesCountLabel}
                  </div>
                )}
                <div
                  className={
                    "bulk-update-row-parent-container bulk-update-row-parent-label-container"
                  }
                >
                  <div
                    className={
                      "bulk-update-row-field-name-container bulk-update-row-field-name-label"
                    }
                  >
                    Field
                  </div>
                  <div
                    className={
                      "bulk-update-row-field-value-container bulk-update-row-field-value-label"
                    }
                  >
                    Value
                  </div>
                  <div
                    className={"bulk-updae-row-action-button-container"}
                  ></div>
                </div>

                {currentBulkUpdateRows?.map((item) => {
                  return (
                    <div className={"bulk-update-row-parent-container"}>
                      <BulkUpdateRowItem
                        {...item}
                        selectedFieldNames={currentBulkUpdateRows
                          .filter((x) => x.fieldName)
                          .map((x) => x.fieldName)}
                      ></BulkUpdateRowItem>
                    </div>
                  );
                })}

                {isAddNewRowVisible() && (
                  <div className={"bulk-update-add-new-field-button-container"}>
                    <MdaeButton
                      id={""}
                      role={"dxButton"}
                      stylingMode={"text"}
                      onClick={() => {
                        addNewBulkUpdateRow(dispatch);
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <div
                          className={
                            "fa fa-plus application-section-primary-color bulk-update-row-action-add-icon"
                          }
                        ></div>
                        <div className={"bulk-update-row-action-add-icon-text"}>
                          Add New Field
                        </div>
                      </div>
                    </MdaeButton>
                  </div>
                )}
              </MdaePopupContent>
            </div>
          );
        }}
        mdaeFooterRender={() => {
          return (
            <MdaePopupFooter>
              <MdaePopupFooterStartButtons>
                <MdaeButton
                  id={"btnBulkUpdateCancel"}
                  role={"secondary"}
                  onClick={() => {
                    hideBulkUpdatePopup(dispatch);
                  }}
                >
                  Cancel
                </MdaeButton>
              </MdaePopupFooterStartButtons>
              <MdaePopupFooterEndButtons>
                <MdaeButton
                  id={"btnBulkUpdateSaveAndClose"}
                  onClick={(params) => {
                    props
                      .saveCallbackHandler(createFinalSaveObject(currentBulkUpdateRows))
                      .then(function(config) {
                        if (config) {
                          if (config.isClosePopup) hideBulkUpdatePopup(dispatch);
                          else if(config.isResetPopup) dispatch({ type: RESET_BULK_UPDATE_ROWS });
                        }
                      });
                  }}
                  role={"primary"}
                >
                  Save and Close
                </MdaeButton>
              </MdaePopupFooterEndButtons>
            </MdaePopupFooter>
          );
        }}
      ></MdaePopup>
    </div>
  );
};
export default BulkUpdatePopup;
